<template>
  <!-- Embedded mode: just the list without the widget wrapper -->
  <div v-if="embedded" class="forms-list embedded">
    <div
      v-for="submission in newSubmissions"
      :key="submission.FormSubmissionIDForWeb"
      class="list-item"
      @click="goToSubmission(submission)"
    >
      <div class="item-avatar form-avatar">
        <i class="fa fa-file-text-o"></i>
      </div>
      <div class="item-content">
        <div class="item-header">
          <span class="item-title">{{ submission.PatientName || 'Unknown Patient' }}</span>
          <span class="item-time">{{ getTimeAgo(submission) }}</span>
        </div>
        <p class="item-preview">{{ submission.FormName || 'Form Submission' }}</p>
        <span v-if="!submission.PatNum" class="item-badge new-patient">Needs Linking</span>
      </div>
    </div>
  </div>

  <!-- Standard mode: full widget -->
  <DashboardWidget
    v-else
    title="New Form Submissions"
    icon="fa fa-file-text-o"
    :view-all-link="{ name: 'Forms' }"
    :loading="loading"
    :is-empty="newSubmissions.length === 0"
    empty-icon="fa fa-file-o"
    empty-title="No new submissions"
    empty-text="No unlinked form submissions in the last 7 days"
  >
    <div class="forms-list">
      <div
        v-for="submission in newSubmissions"
        :key="submission.FormSubmissionIDForWeb"
        class="list-item"
        @click="goToSubmission(submission)"
      >
        <div class="item-avatar form-avatar">
          <i class="fa fa-file-text-o"></i>
        </div>
        <div class="item-content">
          <div class="item-header">
            <span class="item-title">{{ submission.PatientName || 'Unknown Patient' }}</span>
            <span class="item-time">{{ getTimeAgo(submission) }}</span>
          </div>
          <p class="item-preview">{{ submission.FormName || 'Form Submission' }}</p>
          <span v-if="!submission.PatNum" class="item-badge new-patient">Needs Linking</span>
        </div>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import moment from 'moment-timezone';

export default {
  name: 'NewFormsWidget',

  components: {
    DashboardWidget
  },

  props: {
    maxItems: {
      type: Number,
      default: 5
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: true,
      submissions: []
    };
  },

  computed: {
    ...mapState({
      siteID: state => state.customer.siteID
    }),

    newSubmissions() {
      // Filter to show submissions that aren't linked to a patient
      // or are within the last 7 days
      const sevenDaysAgo = moment().subtract(7, 'days');

      return this.submissions
        .filter(sub => {
          // Show unlinked submissions from last 7 days
          const submissionDate = moment(sub.CreatedAt || sub.SubmittedAt);
          return !sub.PatNum && submissionDate.isAfter(sevenDaysAgo);
        })
        .slice(0, this.maxItems);
    }
  },

  async mounted() {
    await this.loadSubmissions();
  },

  watch: {
    newSubmissions: {
      immediate: true,
      handler(val) {
        this.$emit('count-change', val ? val.length : 0);
      }
    }
  },

  methods: {
    ...mapActions('forms', ['getFormSubmissions']),

    async loadSubmissions() {
      this.loading = true;
      try {
        // Exclude large data fields to prevent ResponseSizeTooLarge errors
        const submissions = await this.getFormSubmissions({
          siteID: this.siteID,
          includeFormData: false,
          includeConfigData: false
        });
        this.submissions = submissions || [];
      } catch (error) {
        console.error('Error loading form submissions:', error);
        this.submissions = [];
      } finally {
        this.loading = false;
      }
    },

    getTimeAgo(submission) {
      const date = submission.CreatedAt || submission.SubmittedAt;
      if (!date) return '';

      try {
        return moment(date).fromNow();
      } catch (e) {
        return '';
      }
    },

    goToSubmission(submission) {
      this.$router.push({
        name: 'FormSubmission',
        params: { formSubmissionIDForWeb: submission.FormSubmissionIDForWeb }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.forms-list {
  max-height: 300px;
  overflow-y: auto;
}

.form-avatar {
  background-color: rgba(59, 191, 148, 0.1) !important;
  color: #3abf94 !important;

  i {
    font-size: 1rem;
  }
}
</style>
